import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useDocumentStore from '../../store/documentStore';
import { documentsAPI, documentTypesAPI } from '../../services/api';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
  CheckBadgeIcon,
  PhotoIcon,
  PlayIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  LanguageIcon,
  TableCellsIcon,
  CodeBracketIcon,
  ShareIcon,
  Squares2X2Icon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../../components/ui/StatusBadge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { handleError, handleSuccess } from '../../utils/errorHandler';
import { PageThumbnail } from '../../components/ui/PageThumbnail';
import { getDisplayTitle } from '../../utils/getDisplayTitle';
import StructuredDataPanel from './StructuredDataPanel';



// ---------------------------------------------------------------------------
// Local display helpers — keep simple, single-purpose, no dependencies.
// ---------------------------------------------------------------------------

/**
 * snake_case / kebab-case / SCREAMING_SNAKE → Title Case.
 *   "commercial_registration_certificate" -> "Commercial Registration Certificate"
 *   "salary-certificate" -> "Salary Certificate"
 *   "Bank Statement" -> "Bank Statement" (already nice — left alone)
 */
function titleCase(raw) {
  if (!raw) return '';
  return String(raw)
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Color bucket for an OCR confidence score (0..1).
 *   >= 0.95  ->  green  (high)
 *   >= 0.75  ->  amber  (medium)
 *   < 0.75   ->  red    (low)
 */
function confidenceTier(score) {
  const v = Number(score) || 0;
  if (v >= 0.95) return { dot: 'bg-emerald-400', text: 'text-emerald-400', ring: 'ring-emerald-400/30' };
  if (v >= 0.75) return { dot: 'bg-amber-400', text: 'text-amber-400', ring: 'ring-amber-400/30' };
  return { dot: 'bg-red-400', text: 'text-red-400', ring: 'ring-red-400/30' };
}

/**
 * Human-readable relative time. Returns "just now / 5m ago / 3h ago / 2d ago
 * / 11d ago / Apr 12" — falls back to absolute date past 30 days for clarity.
 * Pair with a `title` attribute carrying the full timestamp.
 */
function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const secs = Math.max(0, Math.floor((now - d) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: now.getFullYear() === d.getFullYear() ? undefined : 'numeric' });
}

// Helper function to format structured data values nicely
function formatStructuredValue(value) {
  if (value === null || value === undefined) return '—';

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    // For arrays of objects (like items), format each item
    return value.map((item, idx) => {
      if (typeof item === 'object' && item !== null) {
        const parts = Object.entries(item)
          .filter(([_, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
          .join(' • ');
        return `${idx + 1}. ${parts}`;
      }
      return `${idx + 1}. ${item}`;
    }).join('\n');
  }

  if (typeof value === 'object') {
    // For objects, format as key-value pairs
    const parts = Object.entries(value)
      .filter(([_, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
    return parts.join('\n') || '—';
  }

  return value.toString();
}



export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    currentDocument,
    fetchDocument,
    deleteDocument,
    isLoading,
    // Global processing state - persists across navigation
    processingDocuments,
    startProcessingWithPolling,
    stopProcessing,
    isDocumentProcessing,
    getProcessingProgress
  } = useDocumentStore();
  const [activeTab, setActiveTab] = useState('pages');
  const [isSharing, setIsSharing] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [textView, setTextView] = useState('arabic'); // 'arabic' or 'english'
  const [pagesPage, setPagesPage] = useState(1);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false);
  const [selectedModel, setSelectedModel] = useState('openai');
  // Multi-document: which detected segment the Structured Data tab is showing.
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugPaddleMeta, setDebugPaddleMeta] = useState({});
  const [loadingPaddleMeta, setLoadingPaddleMeta] = useState(false);
  const PAGES_PER_PAGE = 15;

  // Use global processing state instead of local state (read directly for reactivity)
  const isProcessing = !!processingDocuments[id];
  const processingProgress = processingDocuments[id]?.progress || 0;

  // Editable structured data state
  const [editableData, setEditableData] = useState({});
  const [isEditingStructured, setIsEditingStructured] = useState(false);
  const [isSavingStructured, setIsSavingStructured] = useState(false);

  // On-demand generation state
  const [isTranslating, setIsTranslating] = useState(false);
  const [isGeneratingStructured, setIsGeneratingStructured] = useState(false);

  const progressIntervalRef = useRef(null);
  const previousIsProcessedRef = useRef(false);
  const shareDropdownRef = useRef(null);

  useEffect(() => {
    fetchDocument(id);
  }, [id, fetchDocument]);

  // Reset the selected segment when navigating to a different document.
  useEffect(() => {
    setSelectedSegmentIdx(0);
  }, [id]);

  // NOTE: Polling is now handled in the documentStore, not here.
  // This allows processing to continue even when navigating away.

  // Fetch document types for structured data filtering
  useEffect(() => {
    documentTypesAPI.list().then(res => {
      setDocumentTypes(res.data.results || res.data || []);
    }).catch((error) => {
      console.error('Failed to load document types:', error);
    });
  }, []);

  // Sync editable data with document's structured_json
  useEffect(() => {
    if (currentDocument?.structured_json) {
      setEditableData(currentDocument.structured_json);
    }
  }, [currentDocument?.structured_json]);

  // Normalize name to snake_case for comparison
  const toSnakeCase = (str) => str.toLowerCase().replace(/[\s-]+/g, '_');

  // Check if document matches a defined document type
  const isDefinedDocumentType = () => {
    if (!currentDocument?.document_identity) return false;
    const identity = toSnakeCase(currentDocument.document_identity);
    return documentTypes.some(type => toSnakeCase(type.name) === identity);
  };

  // Get fields for the matched document type
  const getMatchedDocumentTypeFields = useCallback(() => {
    if (!currentDocument?.document_identity) return null;
    const identity = toSnakeCase(currentDocument.document_identity);
    const matchedType = documentTypes.find(type => toSnakeCase(type.name) === identity);
    return matchedType?.fields || null;
  }, [currentDocument?.document_identity, documentTypes]);

  // Filter editable data to only show fields defined in the matched document type schema.
  // Normalizes both schema field names and LLM keys to snake_case for consistent matching.
  const filteredEditableData = useMemo(() => {
    const typeFields = getMatchedDocumentTypeFields();

    // Helper: normalize any field name to snake_case
    const normalizeKey = (name) =>
      name.trim().toLowerCase().replace(/[\s-]+/g, '_');

    // Build a lookup of normalized LLM keys -> values
    const llmLookup = {};
    if (editableData && Object.keys(editableData).length > 0) {
      for (const [key, value] of Object.entries(editableData)) {
        llmLookup[normalizeKey(key)] = value;
      }
    }

    // If no schema fields defined, allow dynamic fallback (SHOW EVERYTHING)
    if (!typeFields || typeFields.length === 0) {
      return editableData || {};
    }

    // Build result from schema first (all fields present), then overlay actual data.
    const result = {};
    for (const field of typeFields) {
      const normName = normalizeKey(field.name);
      // Preserve value from LLM lookup, fallback to empty string for schema compliance
      result[normName] = llmLookup[normName] ?? '';
    }

    // OPTIONAL: Append non-schema dynamic fields at the end, so users see new findings too
    Object.keys(llmLookup).forEach(llmKey => {
      if (!(llmKey in result)) {
        result[llmKey] = llmLookup[llmKey];
      }
    });

    return result;
  }, [editableData, getMatchedDocumentTypeFields]);

  // NOTE: Document status polling is now handled in documentStore.startProcessingWithPolling()
  // The store handles polling even when this component is unmounted

  // Poll for extraction status when pages are not ready
  useEffect(() => {
    let interval;
    if (currentDocument && !currentDocument.pages_ready && currentDocument.pages_count === 0 && !isProcessing) {
      interval = setInterval(async () => {
        const doc = await fetchDocument(id, { silent: true });

        // Check if pages are now ready
        if (doc?.pages_ready && doc?.pages_count > 0) {
          clearInterval(interval);
          toast.success(`${doc.pages_count} page(s) extracted! Ready to process.`, {
            duration: 3000,
            icon: '📄'
          });
        }
      }, 2000); // Poll every 2 seconds for extraction
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentDocument, currentDocument?.pages_ready, currentDocument?.pages_count, isProcessing, id, fetchDocument]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDocument(id);
      handleSuccess('Document deleted successfully');
      navigate('/documents');
    } catch (error) {
      handleError(error, 'Failed to delete document');
    }
  };

  const handleProcess = async () => {
    try {
      // Show immediate feedback
      toast.loading('Starting OCR processing...', {
        id: `process-${id}`,
        duration: 2000
      });

      // Make API call first - pass selected model
      const response = await documentsAPI.process(id, selectedModel);
      console.log('Process API response:', response.data);

      toast.success('Processing started! Tracking real progress...', {
        id: `process-${id}`,
        duration: 5000
      });

      // Start processing with polling in the STORE (continues even when navigating away)
      const documentTitle = currentDocument?.title || `Document ${id}`;
      startProcessingWithPolling(id, documentTitle);

    } catch (error) {
      console.error('Failed to start processing:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to start processing. Please try again.';
      toast.error(errorMsg, { id: `process-${id}` });
      stopProcessing(id);
    }
  };

  const getStatus = () => {
    if (currentDocument?.is_processed) return 'processed';
    if (currentDocument?.has_error) return 'failed';
    if (isProcessing) return 'processing';
    return 'pending';
  };

  // Run Tier-1 AI-generation / tampering checks on the original file
  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const res = await documentsAPI.verify(id);
      if (res.data?.success) {
        const v = res.data.verification;
        toast.success(v?.verdict === 'clear'
          ? 'Verified — no risk markers found'
          : `Verification: ${v?.verdict?.replace(/_/g, ' ')} (risk ${v?.risk_score}/100)`);
        await fetchDocument(id);
      } else {
        toast.error(res.data?.error || 'Verification failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Get combined text from all pages
  const getArabicText = () => {
    if (!currentDocument.pages) return '';
    return currentDocument.pages
      .map((page) => page.arabic_text || page.text || '')
      .filter(Boolean)
      .join('\n\n---\n\n');
  };

  const getEnglishText = () => {
    if (!currentDocument.pages) return '';
    return currentDocument.pages
      .map((page) => page.english_text || '')
      .filter(Boolean)
      .join('\n\n---\n\n');
  };

  // Handle Share - shares document file as attachment
  const handleShare = async () => {
    console.log('[Share] Button clicked');

    if (!currentDocument) {
      console.log('[Share] No document loaded');
      toast.error('Document not loaded');
      return;
    }

    if (!currentDocument.file) {
      console.log('[Share] No file available');
      toast.error('No file available to share');
      return;
    }

    setIsSharing(true);
    console.log('[Share] Starting share process for:', currentDocument.file);

    try {
      // Construct the full file URL
      let fileUrl = currentDocument.file;
      if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
        fileUrl = `${window.location.origin}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      }
      console.log('[Share] File URL:', fileUrl);

      // Get file name from URL
      const fileName = currentDocument.file.split('/').pop() || `document_${currentDocument.id}.pdf`;
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || 'pdf';
      console.log('[Share] File name:', fileName, 'Extension:', fileExtension);

      // Determine MIME type
      const mimeTypes = {
        'pdf': 'application/pdf',
        'png': 'image/png',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'gif': 'image/gif',
        'webp': 'image/webp',
        'tiff': 'image/tiff',
        'tif': 'image/tiff',
      };
      const mimeType = mimeTypes[fileExtension] || 'application/octet-stream';

      // Fetch the file
      console.log('[Share] Fetching file...');
      const response = await fetch(fileUrl, { credentials: 'include' });
      if (!response.ok) {
        console.error('[Share] Fetch failed:', response.status);
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      const blob = await response.blob();
      console.log('[Share] File fetched, size:', blob.size);

      // Create File object
      const file = new File([blob], fileName, { type: mimeType });
      const shareTitle = currentDocument.title || `Document ${currentDocument.id}`;

      // Check if native file sharing is supported (mainly mobile)
      const canShareFiles = navigator.canShare && navigator.canShare({ files: [file] });
      console.log('[Share] Can share files:', canShareFiles);

      if (canShareFiles) {
        try {
          console.log('[Share] Opening native share dialog...');
          await navigator.share({
            title: shareTitle,
            files: [file]
          });
          console.log('[Share] Share completed successfully');
          toast.success('Document shared successfully!');
        } catch (shareError) {
          console.log('[Share] Share error:', shareError.name, shareError.message);
          if (shareError.name !== 'AbortError') {
            // Share failed, download instead
            downloadFile(blob, fileName);
          }
        }
      } else {
        // Desktop browser - download the file
        console.log('[Share] File sharing not supported, downloading instead');
        downloadFile(blob, fileName);
      }
    } catch (error) {
      console.error('[Share] Error:', error);
      toast.error('Failed to share. Downloading file instead...');
      // Try to download even if there was an error
      try {
        const fileUrl = `${window.location.origin}${currentDocument.file.startsWith('/') ? '' : '/'}${currentDocument.file}`;
        const response = await fetch(fileUrl, { credentials: 'include' });
        if (response.ok) {
          const blob = await response.blob();
          const fileName = currentDocument.file.split('/').pop() || `document_${currentDocument.id}.pdf`;
          downloadFile(blob, fileName);
        }
      } catch (downloadError) {
        console.error('[Share] Download fallback also failed:', downloadError);
        toast.error('Unable to share or download file');
      }
    } finally {
      setIsSharing(false);
      console.log('[Share] Process complete');
    }
  };

  // Helper function to download file
  const downloadFile = (blob, fileName) => {
    console.log('[Share] Downloading file:', fileName);
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
    toast.success('File downloaded! Share it via email, WhatsApp, etc.');
  };

  // Handle structured data field change
  const handleStructuredFieldChange = (key, value) => {
    setEditableData(prev => ({ ...prev, [key]: value }));
  };

  // Save structured data
  const handleSaveStructuredData = async () => {
    setIsSavingStructured(true);
    try {
      await documentsAPI.updateStructuredData(id, { structured_data: editableData });
      await fetchDocument(id);
      setIsEditingStructured(false);
      toast.success('Structured data saved successfully!');
    } catch (error) {
      console.error('Failed to save structured data:', error);
      toast.error('Failed to save structured data');
    } finally {
      setIsSavingStructured(false);
    }
  };

  // Cancel editing and reset to original data
  const handleCancelEdit = () => {
    setEditableData(currentDocument.structured_json || {});
    setIsEditingStructured(false);
  };

  // Generate translation on-demand (Celery task with polling)
  const handleGenerateTranslation = async () => {
    setIsTranslating(true);
    try {
      const res = await documentsAPI.translate(id);
      const taskId = res.data.task_id;

      // Poll for completion
      const poll = () => new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const status = await documentsAPI.translateStatus(taskId);
            const d = status.data;
            if (d.status === 'completed') {
              clearInterval(interval);
              resolve(d);
            } else if (d.status === 'error') {
              clearInterval(interval);
              reject(new Error(d.error || 'Translation failed'));
            }
            // still processing — keep polling
          } catch (err) {
            clearInterval(interval);
            reject(err);
          }
        }, 3000);
      });

      await poll();
      await fetchDocument(id);
      toast.success('Translation completed!');
    } catch (error) {
      console.error('Translation failed:', error);
      toast.error(error.message || 'Translation failed. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  // Generate structured data on-demand (Celery task with polling)
  const handleGenerateStructuredData = async () => {
    setIsGeneratingStructured(true);
    try {
      const res = await documentsAPI.generateStructuredData(id);
      const taskId = res.data.task_id;

      // Poll for completion
      const poll = () => new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const status = await documentsAPI.structuredDataStatus(taskId);
            const d = status.data;
            if (d.status === 'completed') {
              clearInterval(interval);
              resolve(d);
            } else if (d.status === 'error') {
              clearInterval(interval);
              reject(new Error(d.error || 'Structured data generation failed'));
            }
          } catch (err) {
            clearInterval(interval);
            reject(err);
          }
        }, 3000);
      });

      await poll();
      await fetchDocument(id);
      toast.success('Structured data generated!');
    } catch (error) {
      console.error('Structured data generation failed:', error);
      await fetchDocument(id);
      toast.error(error.message || 'Structured data generation failed. Please try again.', { duration: 5000 });
    } finally {
      setIsGeneratingStructured(false);
    }
  };

  if (isLoading || !currentDocument) {
    return <LoadingSpinner />;
  }

  const arabicText = getArabicText();
  const englishText = getEnglishText();

  // ── Multi-document segmentation ──────────────────────────────────────────
  // When one uploaded file was detected to contain several documents, the
  // backend fills `segments` (each with its own identity + structured_json +
  // field_meta). We render a per-document selector and feed the chosen
  // segment's data into the (read-only) StructuredDataPanel.
  const segments = Array.isArray(currentDocument.segments) ? currentDocument.segments : [];
  const isMulti = currentDocument.is_multi_document || segments.length > 1;
  const activeSegment = isMulti ? (segments[selectedSegmentIdx] || segments[0]) : null;
  const segmentDoc = activeSegment
    ? {
        ...currentDocument,
        structured_json: activeSegment.structured_json || {},
        field_meta: activeSegment.field_meta || {},
        document_identity: activeSegment.identity,
        pages: (currentDocument.pages || []).filter(
          (p) => p.page_number >= activeSegment.start_page && p.page_number <= activeSegment.end_page
        ),
      }
    : null;

  const tabs = [
    { id: 'pages', label: 'Pages', icon: PhotoIcon, count: currentDocument.pages?.length || 0 },
    { id: 'text', label: 'Extracted Text', icon: LanguageIcon },
    { id: 'structured', label: 'Structured Data', icon: TableCellsIcon },
    { id: 'raw', label: 'Raw JSON', icon: CodeBracketIcon },
  ];

  return (
    <div className="space-y-4 pb-16 lg:pb-0">
      {/* Debug OCR Modal */}
      {showDebugModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-theme-tertiary rounded-2xl shadow-2xl max-w-4xl w-full border border-theme-primary animate-scaleIn max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-theme-primary flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-orange/20 flex items-center justify-center">
                  <CodeBracketIcon className="w-5 h-5 text-brand-orange" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-theme-primary">Raw PaddleOCR Output</h3>
                  <p className="text-xs text-theme-muted">The exact metadata produced by PaddleOCR / PPStructureV3.</p>
                </div>
              </div>
              <button 
                onClick={() => setShowDebugModal(false)}
                className="text-theme-muted hover:text-theme-primary p-2"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 bg-theme-base">
              {loadingPaddleMeta ? (
                <div className="flex items-center justify-center py-10">
                  <div className="spinner-theme w-6 h-6 mr-3" />
                  <span className="text-theme-muted">Loading PaddleOCR metadata...</span>
                </div>
              ) : currentDocument?.pages?.length > 0 ? (
                <div className="space-y-6">
                  {currentDocument.pages.map((page, idx) => {
                    const meta = debugPaddleMeta[page.id] || {};
                    return (
                      <div key={idx} className="bg-theme-elevated p-4 rounded-xl border border-theme-primary">
                        <h4 className="text-md font-bold text-theme-primary mb-2">Page {page.page_number}</h4>
                        {Object.keys(meta).length > 0 ? (
                          <pre className="text-xs text-theme-secondary font-mono bg-black/20 p-4 rounded-lg overflow-x-auto">
                            {JSON.stringify(meta, null, 2)}
                          </pre>
                        ) : (
                          <p className="text-sm text-theme-muted italic">No PaddleOCR metadata found for this page. (It might be an old document or processed as digital PDF).</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-theme-muted text-center py-10">No pages extracted yet.</p>
              )}
            </div>

            <div className="p-4 border-t border-theme-primary flex justify-end">
              <button
                onClick={() => setShowDebugModal(false)}
                className="px-6 py-2 bg-theme-elevated text-theme-primary font-medium rounded-lg hover:bg-theme-surface border border-theme-primary transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reprocess Confirmation Modal */}
      {showReprocessConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-theme-tertiary rounded-2xl shadow-2xl max-w-md w-full border border-theme-primary animate-scaleIn">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <ArrowPathIcon className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-theme-primary">Reprocess Document?</h3>
                  <p className="text-sm text-theme-muted">This will re-run OCR on all pages</p>
                </div>
              </div>

              <div className="bg-theme-elevated rounded-lg p-4 mb-4 border border-theme-primary">
                <ul className="space-y-2 text-sm text-theme-secondary">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">⚠️</span>
                    <span>Previous OCR results will be kept as history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-brand-orange mt-0.5">⏱️</span>
                    <span>Processing may take 20-60 seconds depending on page count</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-status-success mt-0.5">✓</span>
                    <span>You can compare results between different OCR attempts</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowReprocessConfirm(false)}
                  className="flex-1 px-4 py-2.5 bg-theme-elevated text-theme-primary font-medium rounded-lg hover:bg-theme-surface border border-theme-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowReprocessConfirm(false);
                    handleProcess();
                  }}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-brand-orange to-brand-amber text-white font-medium rounded-lg hover:opacity-90 transition-opacity shadow-md"
                >
                  Reprocess Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-theme-tertiary rounded-xl border border-theme-primary shadow-sm overflow-hidden">
        <div className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-theme-elevated transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-theme-muted" />
              </button>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center shadow-md shadow-brand-orange/25">
                  <DocumentTextIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-theme-primary">
                    {getDisplayTitle(currentDocument)}
                  </h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-theme-secondary">
                      {currentDocument.file_type?.toUpperCase()}
                    </span>
                    <span className="text-theme-muted">&bull;</span>
                    <span className="text-xs text-theme-secondary">
                      {currentDocument.pages_count ?? currentDocument.pages?.length ?? 0} pages
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-10 lg:ml-0">
              <Link
                to={`/documents/${id}/crop`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-theme-elevated text-theme-primary text-sm font-medium rounded-lg hover:bg-theme-surface border border-theme-primary transition-colors"
                title="Edit pages: crop images, rotate, run OCR on individual pages"
              >
                <PencilSquareIcon className="w-4 h-4" />
                <span>Edit Pages</span>
              </Link>
              {/* Debug Button */}
              <button
                onClick={async () => {
                  setShowDebugModal(true);
                  if (!Object.keys(debugPaddleMeta).length && currentDocument?.pages?.length) {
                    setLoadingPaddleMeta(true);
                    try {
                      const meta = {};
                      for (const page of currentDocument.pages) {
                        const res = await documentsAPI.getPagePaddleMeta(id, page.id);
                        meta[page.id] = res.data?.paddle_meta || {};
                      }
                      setDebugPaddleMeta(meta);
                    } catch (e) {
                      console.error('Failed to load paddle meta:', e);
                    } finally {
                      setLoadingPaddleMeta(false);
                    }
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-theme-elevated text-theme-primary text-sm font-medium rounded-lg border border-theme-primary transition-colors hover:bg-theme-surface"
                title="View raw PaddleOCR output"
              >
                <CodeBracketIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Debug OCR</span>
              </button>
              {/* Share Button */}
              <button
                onClick={handleShare}
                disabled={isSharing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-theme-elevated text-theme-primary text-sm font-medium rounded-lg border border-theme-primary transition-colors hover:bg-theme-surface disabled:opacity-50 disabled:cursor-not-allowed"
                title="Share this document as attachment"
              >
                {isSharing ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  <ShareIcon className="w-4 h-4" />
                )}
                <span>{isSharing ? 'Sharing...' : 'Share'}</span>
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-status-error/10 text-status-error text-sm font-medium rounded-lg hover:bg-status-error/20 border border-status-error/30 transition-colors"
                title="Delete this document permanently"
              >
                <TrashIcon className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>

        {/* Meta info bar — pill badges for compact, scannable status overview */}
        <div className="px-4 py-2.5 bg-theme-secondary/50 border-t border-theme-primary">
          <div className="flex flex-wrap items-center gap-2">
            {/* Status pill — reuses existing StatusBadge component */}
            <div className="inline-flex items-center">
              <StatusBadge status={getStatus()} />
            </div>

            {/* Document type pill — formatted from snake_case.
                Multi-document files show a "N documents detected" pill instead. */}
            {isMulti ? (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-orange/15 border border-brand-orange/40 text-xs font-semibold text-brand-orange"
                title="This file contains several documents — see the breakdown below"
              >
                <Squares2X2Icon className="w-3.5 h-3.5" />
                {segments.length} documents detected
              </span>
            ) : currentDocument.document_identity && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-theme-tertiary border border-theme-primary/40 text-xs font-medium text-theme-primary"
                title={`Detected document type: ${currentDocument.document_identity}`}
              >
                <CheckBadgeIcon className="w-3.5 h-3.5 text-theme-muted" />
                {titleCase(currentDocument.document_identity)}
              </span>
            )}

            {/* Confidence pill with colored dot — green / amber / red */}
            {currentDocument.confidence != null && (() => {
              const tier = confidenceTier(currentDocument.confidence);
              const pct = (Number(currentDocument.confidence) * 100).toFixed(1);
              return (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-theme-tertiary border border-theme-primary/40 text-xs font-medium text-theme-primary"
                  title={`OCR confidence: ${pct}%`}
                >
                  <span className={`w-2 h-2 rounded-full ${tier.dot} ring-2 ${tier.ring}`} aria-hidden="true" />
                  {pct}%
                </span>
              );
            })()}

            {/* Tier-1 verification pill (AI-generation markers) */}
            {currentDocument.verification ? (() => {
              const v = currentDocument.verification;
              const cfg = {
                high_risk: { label: 'High risk', cls: 'bg-status-error/10 text-status-error border-status-error/40', Icon: ShieldExclamationIcon },
                needs_review: { label: 'Needs review', cls: 'bg-status-warning/10 text-status-warning border-status-warning/40', Icon: ShieldExclamationIcon },
                clear: { label: 'Verified', cls: 'bg-status-success/10 text-status-success border-status-success/40', Icon: ShieldCheckIcon },
              }[v.verdict];
              if (!cfg) return null;
              return (
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${cfg.cls}`}
                  title={`Verification (risk ${v.risk_score}/100):\n${(v.flags || []).map((f) => `• ${f.detail}`).join('\n')}`}
                >
                  <cfg.Icon className="w-3.5 h-3.5" />
                  {cfg.label}
                </span>
              );
            })() : (
              <button
                onClick={handleVerify}
                disabled={isVerifying}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-theme-tertiary border border-theme-primary/40 text-xs font-medium text-theme-secondary hover:text-theme-primary hover:border-brand-orange/40 disabled:opacity-50 transition-colors"
                title="Run AI-generation / tampering checks on the original file"
              >
                {isVerifying ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheckIcon className="w-3.5 h-3.5" />}
                Verify
              </button>
            )}

            {/* Processed-in duration pill */}
            {currentDocument.processing_duration && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-theme-tertiary border border-theme-primary/40 text-xs font-medium text-theme-primary"
                title={`Processing took ${Math.round(currentDocument.processing_duration)}s`}
              >
                <ClockIcon className="w-3.5 h-3.5 text-theme-muted" />
                {currentDocument.processing_duration >= 60
                  ? `${Math.floor(currentDocument.processing_duration / 60)}m ${Math.round(currentDocument.processing_duration % 60)}s`
                  : `${Math.round(currentDocument.processing_duration)}s`}
              </span>
            )}

            {/* Created date pill — relative time with absolute in tooltip */}
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-theme-tertiary border border-theme-primary/40 text-xs font-medium text-theme-primary"
              title={new Date(currentDocument.created_at).toLocaleString('en-US', {
                weekday: 'short', year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            >
              <CalendarIcon className="w-3.5 h-3.5 text-theme-muted" />
              {relativeTime(currentDocument.created_at)}
            </span>
          </div>
        </div>

        {/* Helper tip for unprocessed documents */}
        {!currentDocument.is_processed && !isProcessing && (
          <div className="px-4 py-3 bg-brand-amber/10 border-t border-brand-amber/30">
            <p className="text-sm text-brand-amber">
              <strong>Tip:</strong> Click "Edit Pages" to crop images and run OCR on individual pages, or click "Process" to automatically process all pages at once.
            </p>
          </div>
        )}
      </div>

      {/* Error Banner - Persistent Alert for processing failures (e.g., sizing gates) */}
      {currentDocument?.has_error && currentDocument?.error_message && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-xl p-4 flex items-start gap-3 shadow-sm border-l-4 border-l-status-error animate-fadeIn">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-status-error text-white flex items-center justify-center shadow-sm">
            <span className="text-lg font-black">!</span>
          </div>
          <div className="flex-1 pt-0.5">
            <h4 className="text-sm font-bold text-status-error mb-0.5">Processing Requirement</h4>
            <p className="text-sm text-theme-primary opacity-90 leading-relaxed">{currentDocument.error_message}</p>
            {currentDocument.error_message.toLowerCase().includes('document type') && (
              <div className="mt-3">
                <Link 
                  to="/settings/document-types" 
                  className="inline-flex items-center px-3 py-1.5 bg-status-error text-white text-xs font-bold rounded-lg hover:opacity-90 shadow-sm transition-opacity gap-1"
                >
                  Configure Document Types →
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detected documents overview — only when the file holds several documents */}
      {isMulti && (
        <div className="bg-theme-tertiary rounded-2xl border border-theme-primary shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-theme-primary flex items-center gap-2">
            <Squares2X2Icon className="w-4 h-4 text-brand-orange" />
            <h2 className="text-sm font-bold text-theme-primary">Detected documents</h2>
            <span className="text-xs text-theme-muted">
              This file was automatically split into {segments.length} documents — select one to view its fields.
            </span>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {segments.map((seg, idx) => {
              const isActive = idx === selectedSegmentIdx;
              const conf = Number(seg.confidence) || 0;
              const conf01 = conf > 1 ? conf / 100 : conf;
              const tier = confidenceTier(conf01);
              const fieldCount = seg.structured_json ? Object.keys(seg.structured_json).length : 0;
              return (
                <button
                  key={idx}
                  onClick={() => { setSelectedSegmentIdx(idx); setActiveTab('structured'); }}
                  className={`text-left rounded-xl border p-3 transition-all ${
                    isActive
                      ? 'border-brand-orange bg-brand-orange/10 shadow-sm'
                      : 'border-theme-primary bg-theme-elevated hover:border-brand-orange/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-brand-orange/20 text-brand-orange text-xs font-bold shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-[11px] text-theme-muted font-medium">
                      {seg.start_page === seg.end_page
                        ? `Page ${seg.start_page}`
                        : `Pages ${seg.start_page}–${seg.end_page}`}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-theme-primary truncate" title={titleCase(seg.identity)}>
                    {titleCase(seg.identity) || 'Document'}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="inline-flex items-center gap-1 text-[11px] text-theme-secondary">
                      <span className={`w-1.5 h-1.5 rounded-full ${tier.dot}`} aria-hidden="true" />
                      {(conf01 * 100).toFixed(0)}%
                    </span>
                    <span className="text-theme-muted">&bull;</span>
                    <span className="text-[11px] text-theme-secondary">{fieldCount} fields</span>
                    {seg.classification_matched && (
                      <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-500/15 text-emerald-400 rounded">
                        matched type
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-theme-tertiary rounded-2xl border border-theme-primary shadow-sm overflow-hidden">
        <div className="border-b border-theme-primary">
          <nav className="flex -mb-px overflow-x-auto" role="tablist" aria-label="Document content tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tabpanel-${tab.id}`}
                  id={`tab-${tab.id}`}
                  className={`
                    flex items-center gap-2 px-6 py-4 border-b-2 text-sm font-medium transition-all whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-brand-orange text-brand-orange'
                      : 'border-transparent text-theme-muted hover:text-theme-secondary hover:border-theme-secondary'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className={`
                      px-2 py-0.5 rounded-full text-xs font-semibold
                      ${activeTab === tab.id ? 'bg-brand-orange/20 text-brand-orange' : 'bg-theme-elevated text-theme-muted'}
                    `}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-3" role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
          {activeTab === 'pages' && (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
              {currentDocument.pages?.map((page, index) => {
                const actualIndex = index;
                return (
                  <Link
                    key={page.id}
                    to={`/pages/${page.id}/edit`}
                    className="group relative bg-theme-elevated rounded-lg border border-theme-primary overflow-hidden hover:border-brand-orange/50 hover:shadow-lg hover:shadow-brand-orange/10 transition-all duration-300"
                  >
                    <div className="aspect-[3/4] bg-theme-surface overflow-hidden relative">
                      <PageThumbnail
                        src={page.image}
                        alt={`Page ${actualIndex + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-1 flex items-center justify-between bg-theme-elevated border-t border-theme-primary">
                      <span className="text-[10px] font-medium text-theme-primary">Page {actualIndex + 1}</span>
                      {page.is_processed ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success/15 text-status-success">
                          Done
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-amber/15 text-brand-amber">
                          Pending
                        </span>
                      )}
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-12">
                      <span className="text-white text-xs font-medium">Click to edit</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {activeTab === 'text' && (
            <div className="space-y-3">
              {/* Language toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center bg-theme-elevated rounded-lg p-0.5 border border-theme-primary">
                  <button
                    onClick={() => setTextView('arabic')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${textView === 'arabic'
                      ? 'bg-theme-surface text-brand-orange shadow-sm'
                      : 'text-theme-muted hover:text-theme-secondary'
                      }`}
                  >
                    Transcript
                  </button>
                  <button
                    onClick={() => setTextView('english')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${textView === 'english'
                      ? 'bg-theme-surface text-brand-orange shadow-sm'
                      : 'text-theme-muted hover:text-theme-secondary'
                      }`}
                  >
                    Translation
                  </button>
                </div>
                {(textView === 'arabic' ? arabicText : englishText) && (
                  <button
                    onClick={() => copyToClipboard(textView === 'arabic' ? arabicText : englishText)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-theme-elevated text-theme-primary text-sm font-medium rounded-lg hover:bg-theme-surface border border-theme-primary transition-colors"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    Copy All
                  </button>
                )}
              </div>

              {/* Text content */}
              {textView === 'arabic' ? (
                arabicText ? (
                  <div className="bg-theme-elevated rounded-lg p-4 border border-theme-primary max-h-[50vh] overflow-auto">
                    <p
                      className="text-base text-theme-primary whitespace-pre-wrap leading-relaxed arabic-text"
                      dir="rtl"
                      style={{
                        fontFamily: "'Noto Sans Arabic', 'Amiri', sans-serif",
                        fontSize: '1rem',
                        lineHeight: '1.8',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        minWidth: 0
                      }}
                    >
                      {arabicText}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <LanguageIcon className="w-10 h-10 text-theme-muted mx-auto mb-3" />
                    <p className="text-sm text-theme-muted">No transcript extracted</p>
                    <p className="text-xs text-theme-muted mt-1">Process the document to extract text</p>
                  </div>
                )
              ) : (
                englishText ? (
                  <div className="bg-theme-elevated rounded-lg p-4 border border-theme-primary max-h-[50vh] overflow-auto">
                    <p
                      className="text-base text-theme-primary whitespace-pre-wrap leading-relaxed"
                      style={{
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        minWidth: 0
                      }}
                    >
                      {englishText}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <LanguageIcon className="w-10 h-10 text-theme-muted mx-auto mb-3" />
                    <p className="text-sm text-theme-muted mb-3">No translation available</p>
                    {currentDocument.is_processed && !isTranslating && (
                      <button
                        onClick={handleGenerateTranslation}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white font-medium rounded-lg hover:bg-brand-amber transition-colors"
                      >
                        <LanguageIcon className="w-4 h-4" />
                        Generate Translation
                      </button>
                    )}
                    {isTranslating && (
                      <div className="flex items-center justify-center gap-2 text-brand-orange">
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        <span className="text-sm font-medium">Translating pages...</span>
                      </div>
                    )}
                    {!currentDocument.is_processed && (
                      <p className="text-xs text-theme-muted mt-1">Process the document first to extract text</p>
                    )}
                  </div>
                )
              )}
            </div>
          )}

          {activeTab === 'structured' && (
            isMulti ? (
              <div className="space-y-3">
                {/* Per-document selector — one chip per detected document */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                  {segments.map((seg, idx) => {
                    const active = idx === selectedSegmentIdx;
                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedSegmentIdx(idx)}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          active
                            ? 'bg-brand-orange/15 text-brand-orange border-brand-orange/40'
                            : 'bg-theme-elevated text-theme-secondary border-theme-primary hover:border-brand-orange/40'
                        }`}
                      >
                        {idx + 1}. {titleCase(seg.identity) || 'Document'}
                        <span className="ml-1.5 text-[10px] text-theme-muted">
                          p{seg.start_page}{seg.start_page !== seg.end_page ? `–${seg.end_page}` : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {activeSegment && Object.keys(activeSegment.structured_json || {}).length > 0 ? (
                  <StructuredDataPanel
                    key={selectedSegmentIdx}
                    doc={segmentDoc}
                    readOnly
                    onRefetch={() => fetchDocument(id)}
                  />
                ) : (
                  <div className="text-center py-12 text-theme-muted">
                    <TableCellsIcon className="w-12 h-12 mx-auto mb-3 opacity-60" />
                    <p className="text-sm">
                      No fields were extracted for this document
                      {activeSegment ? ` (${titleCase(activeSegment.identity)})` : ''}.
                    </p>
                    {activeSegment?.errors?.length > 0 && (
                      <p className="text-xs text-status-error mt-2 max-w-md mx-auto">
                        {activeSegment.errors.join(' · ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
            <div>
              {currentDocument.structured_json && Object.keys(filteredEditableData).length > 0 ? (
                <StructuredDataPanel
                  doc={currentDocument}
                  onRefetch={() => fetchDocument(id)}
                />
              ) : (
                <div className="text-center py-12 max-w-md mx-auto">
                  {currentDocument.has_error && currentDocument.error_message ? (
                    <div className="animate-fadeIn">
                      <div className="w-16 h-16 bg-status-error/10 text-status-error rounded-2xl flex items-center justify-center mx-auto mb-4 border border-status-error/20 shadow-sm">
                        <span className="text-3xl font-black">!</span>
                      </div>
                      <h3 className="text-lg font-bold text-theme-primary mb-2">Structured Extraction Stopped</h3>
                      <p className="text-sm text-theme-secondary mb-6 leading-relaxed">
                        {currentDocument.error_message}
                      </p>
                      {currentDocument.error_message.toLowerCase().includes('document type') && (
                        <Link
                          to="/settings/document-types"
                          className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-brand-orange to-brand-amber text-white font-bold rounded-xl shadow-lg hover:opacity-90 transition-all hover:-translate-y-0.5"
                        >
                          Configure Document Types
                        </Link>
                      )}
                    </div>
                  ) : currentDocument.document_identity && !isDefinedDocumentType() ? (
                    <>
                      <TableCellsIcon className="w-12 h-12 text-theme-muted mx-auto mb-4" />
                      <p className="text-theme-muted">
                        Document type &ldquo;{currentDocument.document_identity}&rdquo; is not configured
                      </p>
                      <p className="text-sm text-theme-muted mt-1">
                        Define this document type in{' '}
                        <Link
                          to="/settings/document-types"
                          className="text-brand-orange hover:text-brand-amber underline font-medium"
                        >
                          Document Types
                        </Link>
                        {' '}to see structured data
                      </p>
                    </>
                  ) : (
                    <>
                      <TableCellsIcon className="w-12 h-12 text-theme-muted mx-auto mb-4" />
                      <p className="text-theme-muted mb-3">
                        {currentDocument.structured_json && Object.keys(currentDocument.structured_json).length > 0
                          ? 'No matching document type schema found'
                          : 'No structured data generated yet'}
                      </p>
                      {currentDocument.is_processed && !isGeneratingStructured && (
                        <button
                          onClick={handleGenerateStructuredData}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white font-medium rounded-lg hover:bg-brand-amber transition-colors"
                        >
                          <TableCellsIcon className="w-4 h-4" />
                          Generate Structured Data
                        </button>
                      )}
                      {isGeneratingStructured && (
                        <div className="flex items-center justify-center gap-2 text-brand-orange">
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          <span className="text-sm font-medium">Generating structured data...</span>
                        </div>
                      )}
                      {!currentDocument.is_processed && (
                        <p className="text-xs text-theme-muted mt-1">Process the document first to extract text</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            )
          )}

          {activeTab === 'raw' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => copyToClipboard(JSON.stringify(currentDocument, null, 2))}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-theme-elevated text-theme-primary font-medium rounded-xl hover:bg-theme-surface border border-theme-primary transition-colors"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                  Copy JSON
                </button>
              </div>
              <pre className="bg-theme-elevated rounded-xl p-4 text-sm text-theme-secondary overflow-auto max-h-[500px] border border-theme-primary">
                {JSON.stringify(currentDocument, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
